import {fromString} from "../../../ToastMessage";
import {LOADING_INDICATOR_DELAY_MS} from "../../../../Library/LoadingIndicator";
import {synchronizeFilterFields, getGlobalFilterIndex} from "../Preview/Synchronizer";
import {initializeTemplateFields} from "../../TemplateFields";

const syncActiveCondition = (container) => {
    const operator  = container.querySelector('[name="operator"]');
    const condition = container.querySelector('.condition:not(.key-field)');

    if (!operator || !condition) {
        return;
    }

    const template = container.querySelector(`template[data-filter-value="${operator.value}"]`);

    if (template) {
        condition.dataset.activeCondition = template.dataset.filterCondition;
    }
};

const filterRequestControllers = new WeakMap();

const createFilterUrl = (field, parameters) => {
    const urlTemplate = document.querySelector('[data-create-filter-url]')?.dataset.createFilterUrl;

    if (!urlTemplate) {
        return null;
    }

    const url = new URL(urlTemplate.replace('{0}', encodeURIComponent(field)), window.location.origin);

    Object.entries(parameters).forEach(([name, value]) => url.searchParams.set(name, value));

    return url;
};

document.addEventListener('input', async (event) => {
    const selector = event.target.closest('.automation-filter select[name="filterFields"]');

    if (!selector) {
        return;
    }

    const container = selector.closest('.automation-filter-contents');

    const target = selector.closest('form')
        ?.querySelector('[name="targetEntity"], [name="currentFilterTarget"]')?.value ?? 'order';
    const url    = createFilterUrl(selector.value, {target});

    if (!url) {
        return;
    }

    filterRequestControllers.get(container)?.abort();

    const controller = new AbortController();
    filterRequestControllers.set(container, controller);

    container.classList.add('loading');

    const clearStaleConditions = setTimeout(() => {
        container.querySelectorAll('.condition').forEach(el => el.remove());
        container.querySelectorAll('template[data-filter-value]').forEach(template => template.remove());
    }, LOADING_INDICATOR_DELAY_MS);

    try {
        const response = await fetch(url, {
            signal: controller.signal,
        });

        if (response.status === 400) {
            container.querySelectorAll('.condition').forEach(el => el.remove());

            fromString(await response.text());

            return;
        }

        const conditionFields = new DOMParser().parseFromString(await response.text(), 'text/html');

        container.querySelectorAll('.condition').forEach(el => el.remove());
        container.querySelectorAll('template[data-filter-value]').forEach(template => template.remove());

        const previewTemplate = conditionFields.querySelector('[data-filter-preview]');

        if (previewTemplate) {
            const automationFilter = container.closest('.automation-filter');
            const preview          = document.querySelector('.workflow-container .automation.preview');

            if (preview) {
                const filterIndex   = getGlobalFilterIndex(automationFilter);
                const filterPreview = preview.querySelectorAll('.filter-preview-group ol li')[filterIndex];

                if (filterPreview) {
                    filterPreview.innerHTML = previewTemplate.innerHTML;

                    const filterName = selector.selectedOptions[0]?.text || '...';

                    filterPreview.querySelectorAll('span').forEach((span, index) => {
                        span.textContent = index === 0 ? filterName : '';
                    });
                }
            }
        }

        conditionFields.querySelectorAll('template[data-filter-value]').forEach(template => {
            container.appendChild(document.importNode(template, true));
        });

        const keyTemplate = conditionFields.querySelector('[data-filter-key]');

        if (keyTemplate) {
            container.appendChild(keyTemplate.content.cloneNode(true));
            container.querySelector('.condition:last-of-type').classList.add('key-field');
            container.querySelector('.key-field input')?.focus();
        }

        // Composite filters render with no conditions until a scope/key is chosen (State A):
        // only build the operator + value row when the fetched fragment actually has value templates.
        const firstValueTemplate = container.querySelector('template[data-filter-value]');

        if (firstValueTemplate) {
            container.appendChild(conditionFields.querySelector('[data-filter-operator]').content.cloneNode(true));

            container.querySelector('.condition:not(.key-field)').appendChild(
                firstValueTemplate.content.cloneNode(true),
            );
        }

        syncActiveCondition(container);

        initializeTemplateFields(container);

        synchronizeFilterFields(selector.closest('.automation-filter'));
    } catch (error) {
        if (error.name !== 'AbortError') {
            throw error;
        }
    } finally {
        clearTimeout(clearStaleConditions);
        container.classList.remove('loading');

        if (filterRequestControllers.get(container) === controller) {
            filterRequestControllers.delete(container);
        }
    }
});

const clearOperatorAndValue = (container) => {
    container.querySelectorAll('.condition:not(.key-field)').forEach(el => el.remove());
    container.querySelectorAll('template[data-filter-value]').forEach(template => template.remove());
};

document.addEventListener('input', async (event) => {
    // Rebuild on a change of EITHER cascade select. The server filters the target options by the
    // selected scope and builds the operators (integration fields' depend on the key, workflow-variable's on
    // the type). On a scope change we also reset + re-inject the freshly-filtered target options.
    const scopeChanged = event.target.closest('.automation-filter [data-composite-key] select[name="scope"]') !== null;
    const cascadeSelect = scopeChanged
        ? event.target.closest('.automation-filter [data-composite-key] select[name="scope"]')
        : event.target.closest('.automation-filter [data-composite-key] select[name="key"]');

    if (!cascadeSelect) {
        return;
    }

    const automationFilter = cascadeSelect.closest('.automation-filter');
    const container        = automationFilter.querySelector('.automation-filter-contents');
    const filterType       = container.querySelector('select[name="filterFields"]')?.value;
    const scope            = container.querySelector('[data-composite-key] select[name="scope"]')?.value ?? '';
    // A scope change invalidates the previously-selected target, so reset it for the request.
    const key              = scopeChanged
        ? ''
        : (container.querySelector('[data-composite-key] select[name="key"]')?.value ?? '');
    const target           = automationFilter.closest('form')
        ?.querySelector('[name="targetEntity"], [name="currentFilterTarget"]')?.value ?? 'order';
    const url              = filterType ? createFilterUrl(filterType, {target, scope, key}) : null;

    if (!url) {
        return;
    }

    // A target cleared without a scope change (deselected): collapse back to no operator/value.
    if (!scopeChanged && key === '') {
        clearOperatorAndValue(container);
        synchronizeFilterFields(automationFilter);

        return;
    }

    filterRequestControllers.get(container)?.abort();

    const controller = new AbortController();
    filterRequestControllers.set(container, controller);

    try {
        const response = await fetch(url, {signal: controller.signal});

        if (!response.ok) {
            return;
        }

        const fields = new DOMParser().parseFromString(await response.text(), 'text/html');

        const responseKey = fields.querySelector('[data-filter-key]')?.content;

        // Source changed: swap the target select for the freshly server-filtered one, and refresh the
        // label-template map (its keys are the new integration's fields).
        if (scopeChanged) {
            const newTarget     = responseKey?.querySelector('select[name="key"]')?.closest('od-select');
            const currentTarget = container.querySelector('[data-composite-key] select[name="key"]')?.closest('.select-container');

            if (newTarget && currentTarget) {
                currentTarget.replaceWith(document.importNode(newTarget, true));
            }

            const newWrapper     = responseKey?.querySelector('[data-composite-key]');
            const currentWrapper = container.querySelector('[data-composite-key]');

            if (newWrapper && currentWrapper && newWrapper.dataset.labelTemplates !== undefined) {
                currentWrapper.dataset.labelTemplates = newWrapper.dataset.labelTemplates;
            }
        }

        // Show/hide the parameter input(s) for the freshly selected field (empty on a scope reset).
        const newParameter     = responseKey?.querySelector('[data-key-parameter]');
        const currentParameter = container.querySelector('[data-composite-key] [data-key-parameter]');

        if (newParameter && currentParameter) {
            currentParameter.replaceChildren(...document.importNode(newParameter, true).childNodes);
        }

        clearOperatorAndValue(container);

        fields.querySelectorAll('template[data-filter-value]').forEach(template => {
            container.appendChild(document.importNode(template, true));
        });

        const operatorTemplate   = fields.querySelector('[data-filter-operator]');
        const firstValueTemplate = container.querySelector('template[data-filter-value]');

        if (operatorTemplate && firstValueTemplate) {
            container.appendChild(operatorTemplate.content.cloneNode(true));
            container.querySelector('.condition:not(.key-field)').appendChild(
                firstValueTemplate.content.cloneNode(true),
            );

            syncActiveCondition(container);

            initializeTemplateFields(container);
        }

        synchronizeFilterFields(automationFilter);
    } catch (error) {
        if (error.name !== 'AbortError') {
            throw error;
        }
    } finally {
        if (filterRequestControllers.get(container) === controller) {
            filterRequestControllers.delete(container);
        }
    }
});

document.addEventListener('input', (event) => {
    const operatorField = event.target.closest('[name="operator"]');

    if (!operatorField) {
        return;
    }

    const container = operatorField.closest('.automation-filter-contents');
    const template  = container.querySelector(`template[data-filter-value="${operatorField.value}"]`);

    if (!template) {
        return;
    }

    const condition = operatorField.closest('.condition');

    const automationFilter = operatorField.closest('.automation-filter');

    if (condition.dataset.activeCondition === template.dataset.filterCondition) {
        synchronizeFilterFields(automationFilter);

        return;
    }

    const valueField = template.content.cloneNode(true);

    while (condition.children.length > 1) {
        condition.lastElementChild.remove();
    }

    condition.appendChild(valueField);
    condition.dataset.activeCondition = template.dataset.filterCondition;

    initializeTemplateFields(condition);

    synchronizeFilterFields(automationFilter);
});

export const syncActiveConditions = (root) => {
    root.querySelectorAll('.automation-filter-contents').forEach(syncActiveCondition);
};
